// スタッフ情報管理カスタムフック
// CRUD操作、バリデーション、状態管理

import { useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';
import { StaffInfo, StaffRole, StaffFilterOptions, StaffValidationResult } from '../types/staff';
import { validateStaffInfo, normalizeStaffInput } from '../utils/staffValidation';

interface UseStaffInfoOptions {
  autoLoad?: boolean;
  initialFilter?: StaffFilterOptions;
}

interface UseStaffInfoResult {
  // データ
  staffList: StaffInfo[];
  currentStaff: StaffInfo | null;
  filteredStaff: StaffInfo[];
  
  // 状態
  isLoading: boolean;
  error: string | null;
  validationErrors: StaffValidationResult | null;
  
  // フィルター・検索
  filters: StaffFilterOptions;
  searchQuery: string;
  
  // CRUD操作
  loadStaffList: () => Promise<void>;
  getStaffById: (id: string) => Promise<StaffInfo | null>;
  createStaff: (staffData: Omit<StaffInfo, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string | null>;
  updateStaff: (id: string, staffData: Partial<StaffInfo>) => Promise<boolean>;
  deleteStaff: (id: string) => Promise<boolean>;
  
  // 状態管理
  setCurrentStaff: (staff: StaffInfo | null) => void;
  setFilters: (filters: StaffFilterOptions) => void;
  setSearchQuery: (query: string) => void;
  clearError: () => void;
  
  // バリデーション
  validateStaffData: (staffData: Partial<StaffInfo>) => StaffValidationResult;
  
  // ユーティリティ
  getAvailableStaff: (date: Date, timeRange?: { start: string; end: string }) => StaffInfo[];
  getStaffByRole: (role: StaffRole) => StaffInfo[];
  getStaffBySkill: (skill: string) => StaffInfo[];
}

// モックデータ（実際のAPIと置き換え予定）
const mockStaffData: StaffInfo[] = [
  {
    id: 'staff-001',
    basicInfo: {
      firstName: '太郎',
      lastName: '田中',
      email: 'tanaka@hitoyasumi.com',
      phone: '090-1234-5678',
      hireDate: new Date('2023-04-01'),
      role: 'manager',
      status: 'active',
    },
    qualifications: {
      licenses: ['美容師免許', 'アロマ検定1級'],
      skills: ['ドライヘッドスパ', 'フェイシャル', 'アロマトリートメント'],
      experience: 8,
      specialties: ['ヘッドスパ専門', 'リラクゼーション'],
    },
    workSchedule: {
      availableDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
      workingHours: { start: '09:00', end: '18:00' },
      breakTime: { start: '12:00', end: '13:00' },
    },
    preferences: {
      maxClientsPerDay: 8,
      preferredServices: ['ドライヘッドスパ', 'フェイシャル'],
      notes: '顧客対応が丁寧で経験豊富です。',
    },
    createdAt: new Date('2023-04-01'),
    updatedAt: new Date('2023-04-01'),
    updatedBy: 'system',
  },
  {
    id: 'staff-002',
    basicInfo: {
      firstName: '花子',
      lastName: '佐藤',
      email: 'sato@hitoyasumi.com',
      phone: '080-9876-5432',
      hireDate: new Date('2023-06-15'),
      role: 'staff',
      status: 'active',
    },
    qualifications: {
      licenses: ['美容師免許'],
      skills: ['ドライヘッドスパ', 'アイヘッドケア'],
      experience: 3,
      specialties: ['眼精疲労ケア'],
    },
    workSchedule: {
      availableDays: ['tue', 'wed', 'thu', 'fri', 'sat'],
      workingHours: { start: '10:00', end: '19:00' },
      breakTime: { start: '14:00', end: '15:00' },
    },
    preferences: {
      maxClientsPerDay: 6,
      preferredServices: ['ドライヘッドスパ'],
      notes: '新人ですが技術習得が早いです。',
    },
    createdAt: new Date('2023-06-15'),
    updatedAt: new Date('2023-06-15'),
    updatedBy: 'tanaka',
  },
  {
    id: 'staff-003',
    basicInfo: {
      firstName: '次郎',
      lastName: '鈴木',
      email: 'suzuki@hitoyasumi.com',
      phone: '070-1111-2222',
      hireDate: new Date('2023-08-01'),
      role: 'trainee',
      status: 'active',
    },
    qualifications: {
      licenses: [],
      skills: ['基本ヘッドマッサージ'],
      experience: 0.5,
      specialties: [],
    },
    workSchedule: {
      availableDays: ['mon', 'wed', 'fri'],
      workingHours: { start: '10:00', end: '17:00' },
      breakTime: { start: '12:30', end: '13:30' },
    },
    preferences: {
      maxClientsPerDay: 3,
      preferredServices: ['基本ヘッドマッサージ'],
      notes: '研修中。先輩スタッフの補助業務中心。',
    },
    createdAt: new Date('2023-08-01'),
    updatedAt: new Date('2023-08-01'),
    updatedBy: 'tanaka',
  },
];

export const useStaffInfo = (options: UseStaffInfoOptions = {}): UseStaffInfoResult => {
  const { autoLoad = true, initialFilter = {} } = options;
  
  // 状態管理
  const [staffList, setStaffList] = useState<StaffInfo[]>([]);
  const [currentStaff, setCurrentStaff] = useState<StaffInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<StaffValidationResult | null>(null);
  const [filters, setFilters] = useState<StaffFilterOptions>(initialFilter);
  const [searchQuery, setSearchQuery] = useState('');

  // フィルタリング済みスタッフリスト
  const filteredStaff = staffList.filter(staff => {
    // テキスト検索
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const fullName = `${staff.basicInfo.lastName}${staff.basicInfo.firstName}`.toLowerCase();
      const email = staff.basicInfo.email.toLowerCase();
      const skills = staff.qualifications.skills.join(' ').toLowerCase();
      
      if (!fullName.includes(query) && !email.includes(query) && !skills.includes(query)) {
        return false;
      }
    }

    // ロールフィルター
    if (filters.role && staff.basicInfo.role !== filters.role) {
      return false;
    }

    // ステータスフィルター
    if (filters.status && staff.basicInfo.status !== filters.status) {
      return false;
    }

    // スキルフィルター
    if (filters.skills && filters.skills.length > 0) {
      const hasSkill = filters.skills.some(skill => 
        staff.qualifications.skills.includes(skill)
      );
      if (!hasSkill) {
        return false;
      }
    }

    // 勤務日フィルター
    if (filters.availableDate) {
      const dayOfWeek = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][filters.availableDate.getDay()];
      if (!staff.workSchedule.availableDays.includes(dayOfWeek)) {
        return false;
      }
    }

    return true;
  });

  // スタッフリストの読み込み
  const loadStaffList = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 実際のAPIコールに置き換え予定
      await new Promise(resolve => setTimeout(resolve, 500)); // モック遅延
      setStaffList(mockStaffData);
    } catch (err) {
      setError('スタッフリストの読み込みに失敗しました');
      logger.error('Error loading staff list:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ID指定でスタッフ取得
  const getStaffById = useCallback(async (id: string): Promise<StaffInfo | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 実際のAPIコールに置き換え予定
      await new Promise(resolve => setTimeout(resolve, 200)); // モック遅延
      const staff = mockStaffData.find(s => s.id === id) || null;
      if (staff) {
        setCurrentStaff(staff);
      }
      return staff;
    } catch (err) {
      setError('スタッフ情報の取得に失敗しました');
      logger.error('Error getting staff by ID:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // スタッフ作成
  const createStaff = useCallback(async (
    staffData: Omit<StaffInfo, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // バリデーション
      const normalizedData = normalizeStaffInput(staffData);
      const validation = validateStaffInfo(normalizedData);
      
      if (!validation.isValid) {
        setValidationErrors(validation);
        return null;
      }
      
      // 実際のAPIコールに置き換え予定
      await new Promise(resolve => setTimeout(resolve, 500)); // モック遅延
      
      const newId = `staff-${Date.now()}`;
      const newStaff: StaffInfo = {
        ...staffData,
        id: newId,
        createdAt: new Date(),
        updatedAt: new Date(),
        updatedBy: 'current-user', // 実際のユーザーIDに置き換え
      };
      
      setStaffList(prev => [...prev, newStaff]);
      setValidationErrors(null);
      
      return newId;
    } catch (err) {
      setError('スタッフの作成に失敗しました');
      logger.error('Error creating staff:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // スタッフ更新
  const updateStaff = useCallback(async (
    id: string,
    staffData: Partial<StaffInfo>
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // バリデーション
      const normalizedData = normalizeStaffInput(staffData);
      const validation = validateStaffInfo(normalizedData);
      
      if (!validation.isValid) {
        setValidationErrors(validation);
        return false;
      }
      
      // 実際のAPIコールに置き換え予定
      await new Promise(resolve => setTimeout(resolve, 500)); // モック遅延
      
      setStaffList(prev => prev.map(staff => 
        staff.id === id 
          ? { 
              ...staff, 
              ...staffData, 
              updatedAt: new Date(),
              updatedBy: 'current-user' // 実際のユーザーIDに置き換え
            }
          : staff
      ));
      
      // 現在表示中のスタッフも更新
      if (currentStaff && currentStaff.id === id) {
        setCurrentStaff(prev => prev ? { ...prev, ...staffData, updatedAt: new Date() } : null);
      }
      
      setValidationErrors(null);
      return true;
    } catch (err) {
      setError('スタッフ情報の更新に失敗しました');
      logger.error('Error updating staff:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentStaff]);

  // スタッフ削除
  const deleteStaff = useCallback(async (id: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 実際のAPIコールに置き換え予定
      await new Promise(resolve => setTimeout(resolve, 300)); // モック遅延
      
      setStaffList(prev => prev.filter(staff => staff.id !== id));
      
      // 現在表示中のスタッフが削除対象の場合はクリア
      if (currentStaff && currentStaff.id === id) {
        setCurrentStaff(null);
      }
      
      return true;
    } catch (err) {
      setError('スタッフの削除に失敗しました');
      logger.error('Error deleting staff:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentStaff]);

  // バリデーション関数
  const validateStaffData = useCallback((staffData: Partial<StaffInfo>): StaffValidationResult => {
    const normalizedData = normalizeStaffInput(staffData);
    return validateStaffInfo(normalizedData);
  }, []);

  // 利用可能スタッフの取得
  const getAvailableStaff = useCallback((
    date: Date,
    timeRange?: { start: string; end: string }
  ): StaffInfo[] => {
    const dayOfWeek = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][date.getDay()];
    
    return staffList.filter(staff => {
      // 勤務日チェック
      if (!staff.workSchedule.availableDays.includes(dayOfWeek)) {
        return false;
      }
      
      // アクティブなスタッフのみ
      if (staff.basicInfo.status !== 'active') {
        return false;
      }
      
      // 時間範囲チェック（オプション）
      if (timeRange) {
        // 簡単な時間チェック（実際はより複雑な予約重複チェックが必要）
        const workStart = staff.workSchedule.workingHours.start;
        const workEnd = staff.workSchedule.workingHours.end;
        
        if (timeRange.start < workStart || timeRange.end > workEnd) {
          return false;
        }
      }
      
      return true;
    });
  }, [staffList]);

  // ロール別スタッフ取得
  const getStaffByRole = useCallback((role: StaffRole): StaffInfo[] => {
    return staffList.filter(staff => staff.basicInfo.role === role);
  }, [staffList]);

  // スキル別スタッフ取得
  const getStaffBySkill = useCallback((skill: string): StaffInfo[] => {
    return staffList.filter(staff => staff.qualifications.skills.includes(skill));
  }, [staffList]);

  // エラークリア
  const clearError = useCallback(() => {
    setError(null);
    setValidationErrors(null);
  }, []);

  // 初期データ読み込み
  useEffect(() => {
    if (autoLoad) {
      loadStaffList();
    }
  }, [autoLoad, loadStaffList]);

  return {
    // データ
    staffList,
    currentStaff,
    filteredStaff,
    
    // 状態
    isLoading,
    error,
    validationErrors,
    
    // フィルター・検索
    filters,
    searchQuery,
    
    // CRUD操作
    loadStaffList,
    getStaffById,
    createStaff,
    updateStaff,
    deleteStaff,
    
    // 状態管理
    setCurrentStaff,
    setFilters,
    setSearchQuery,
    clearError,
    
    // バリデーション
    validateStaffData,
    
    // ユーティリティ
    getAvailableStaff,
    getStaffByRole,
    getStaffBySkill,
  };
};